/**
 * @fileoverview Product media zoom wrapper: pan-and-zoom for a single image inside the PDP gallery.
 *
 * Custom element: <drag-zoom-wrapper>. Expects a single <img> and optional hint elements
 * (.product-media__drag-zoom-wrapper__hint, .product-media__drag-zoom-wrapper__wheel-hint).
 *
 * Behavior:
 * - Desktop: mouse drag to pan when zoomed; click to zoom in 2x / click again to reset; wheel to zoom (1x–5x).
 * - Touch: single-finger drag when zoomed; tap to zoom in 2x or reset; two-finger pinch to zoom; two-finger pan when zoomed.
 *
 * CSS custom properties (set on the element):
 * - --drag-zoom-translate-x, --drag-zoom-translate-y: translation in px (used with scale for transform).
 * - --drag-zoom-scale: scale factor (1–5).
 *
 * Public API: resetZoom() to reset to 1x and center. Used by zoom-dialog when closing/opening.
 */
class DragZoomWrapper extends HTMLElement {
  /** @type {HTMLImageElement | null} */
  #image = null;
  #isDragging = false;
  #hasDragged = false;
  #startX = 0;
  #startY = 0;
  #lastX = 0;
  #lastY = 0;
  #scale = 1;
  #startScale = 1;
  #startDistance = 0;
  #touchStartTime = 0;
  #touchStartX = 0;
  #touchStartY = 0;
  #pinchCenterX = 0;
  #pinchCenterY = 0;
  #twoFingerDragStartX = 0;
  #twoFingerDragStartY = 0;
  #twoFingerDragLastX = 0;
  #twoFingerDragLastY = 0;
  #isTwoFingerDrag = false;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #hintTimeout = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #hintHideTimeout = null;
  /** @type {HTMLElement | null} */
  #hintElement = null;
  /** @type {HTMLElement | null} */
  #wheelHintElement = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #wheelHintTimeout = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #wheelHintHideTimeout = null;
  /** @type {boolean} */
  #pinchAnchorCommitted = false;

  /** Called when the element is added to the DOM. Sets up CSS vars, refs, and event listeners. */
  connectedCallback() {
    this.style.setProperty("--drag-zoom-translate-x", "0px");
    this.style.setProperty("--drag-zoom-translate-y", "0px");
    this.style.setProperty("--drag-zoom-scale", "1");

    this.#image = this.querySelector("img");
    if (!this.#image) return;

    const hintEnabled = this.getAttribute("data-hint-enabled") !== "false";
    if (hintEnabled) {
      this.#hintElement = this.querySelector(
        ".product-media__drag-zoom-wrapper__hint"
      );
      this.#wheelHintElement = this.querySelector(
        ".product-media__drag-zoom-wrapper__wheel-hint"
      );
    }

    this.#initUnifiedEventListeners();

    if (hintEnabled) {
      this.#initHintVisibility();
      this.#initWheelHintVisibility();
    }
  }

  /** Binds mouse (down/move/up, click), touch (start/move/end on window for move), and wheel (capture) for zoom/pan. */
  #initUnifiedEventListeners() {
    this.addEventListener("mousedown", this.#handlePointerDown);
    window.addEventListener("mousemove", this.#handlePointerMove);
    window.addEventListener("mouseup", this.#handlePointerUp);
    this.addEventListener("click", this.#handleClick);

    // Touch drag/pinch
    this.addEventListener("touchstart", this.#handlePointerDown, {
      passive: false,
    });
    // Use window for touchmove so it works even when finger moves outside element
    window.addEventListener("touchmove", this.#handlePointerMove, {
      passive: false,
    });
    window.addEventListener("touchend", this.#handlePointerUp);

    // Wheel zoom
    this.addEventListener("wheel", this.#handleWheelZoom, {
      passive: false,
      capture: true,
    });

  }

  /** Schedules show/hide of the drag/pinch hint based on zoom state and mouse movement (mobile: pinch hint when not zoomed). */
  #initHintVisibility() {
    // Show hint after inactivity when zoomed
    // Also schedule when mouse stops moving for a while
    // On mobile, also show pinch hint when not zoomed
    const isMobile = window.matchMedia("(max-width: 749px)").matches;
    if (isMobile) {
      // Schedule hint initially on mobile
      this.#scheduleHint();
    }
    
    /** @type {ReturnType<typeof setTimeout> | null} */
    let mouseMoveTimeout = null;
    this.addEventListener("mousemove", () => {
      this.#hideHint();
      if (mouseMoveTimeout) {
        clearTimeout(mouseMoveTimeout);
      }
      // Schedule hint after mouse stops moving for 4 seconds
      mouseMoveTimeout = setTimeout(() => {
        this.#scheduleHint();
      }, 4000);
    });

    this.addEventListener("mouseleave", () => {
      if (mouseMoveTimeout) {
        clearTimeout(mouseMoveTimeout);
        mouseMoveTimeout = null;
      }
      this.#scheduleHint();
    });
  }

  /** Schedules the drag/pinch hint to show after a delay when zoomed (or on mobile when not zoomed). */
  #scheduleHint() {
    // Check if we're on mobile
    const isMobile = window.matchMedia("(max-width: 749px)").matches;
    // Show hint when zoomed (desktop/tablet) or always on mobile (for pinch hint)
    const shouldShowHint = (this.#scale > 1) || (isMobile && this.#scale === 1);
    
    if (shouldShowHint && !this.#isDragging) {
      this.#hintTimeout = setTimeout(() => {
        const stillShouldShow = (this.#scale > 1) || (isMobile && this.#scale === 1);
        if (stillShouldShow && !this.#isDragging && this.#hintElement) {
          // Hint will be shown via CSS animation
          if (this.#hintElement instanceof HTMLElement) {
            this.#hintElement.style.animation =
              "showDragHint 0.5s ease-out forwards";
            this.#hintElement.style.opacity = "1";

            // Hide after 4 seconds, then reschedule to show again
            this.#hintHideTimeout = setTimeout(() => {
              this.#hideHint();
              // Reschedule to show again after 2 seconds
              setTimeout(() => {
                const shouldReschedule = (this.#scale > 1) || (isMobile && this.#scale === 1);
                if (shouldReschedule && !this.#isDragging) {
                  this.#scheduleHint();
                }
              }, 2000);
            }, 4000);
          }
        }
      }, 2000);
    }
  }

  /** Clears hint timeouts and hides the hint element. */
  #hideHint() {
    clearTimeout(this.#hintTimeout);
    this.#hintTimeout = null;
    if (this.#hintHideTimeout) {
      clearTimeout(this.#hintHideTimeout);
      this.#hintHideTimeout = null;
    }
    if (this.#hintElement && this.#hintElement instanceof HTMLElement) {
      this.#hintElement.style.animation = "none";
      this.#hintElement.style.opacity = "0";
    }
  }

  /**
   * Wheel zoom: zoom in/out (1x–5x) with cursor position as pivot.
   * @param {WheelEvent} event
   */
  #handleWheelZoom = (event) => {
    event.preventDefault();
    this.#hideHint();
    this.#hideWheelHint();
    
    if (!this.#image) return;
    
    const zoomIntensity = 0.1;
    const delta = event.deltaY < 0 ? 1 + zoomIntensity : 1 - zoomIntensity;
    const oldScale = this.#scale; // Store old scale before updating
    const newScale = Math.max(1, Math.min(5, this.#scale * delta)); // min scale = 1
    
    // Update scale
    this.#scale = newScale;
    this.style.setProperty("--drag-zoom-scale", String(this.#scale));
    
    // If zoomed out to minimum (scale = 1), center the image
    if (this.#scale === 1) {
      this.#lastX = 0;
      this.#lastY = 0;
      this.style.setProperty("--drag-zoom-translate-x", "0px");
      this.style.setProperty("--drag-zoom-translate-y", "0px");
    } else {
      // Get mouse position relative to the wrapper
      const wrapperRect = this.getBoundingClientRect();
      const mouseX = event.clientX - wrapperRect.left;
      const mouseY = event.clientY - wrapperRect.top;
      
      // Calculate the point in image coordinates (before zoom) using old scale
      const imageX = (mouseX - wrapperRect.width / 2 - this.#lastX) / oldScale;
      const imageY = (mouseY - wrapperRect.height / 2 - this.#lastY) / oldScale;
      
      // Adjust translation to keep the point under the mouse in the same place
      this.#lastX = mouseX - wrapperRect.width / 2 - imageX * this.#scale;
      this.#lastY = mouseY - wrapperRect.height / 2 - imageY * this.#scale;
    }
    
    this.#updateZoomState();
    this.#clampTranslation();
    this.#scheduleHint();
  };

  /**
   * Pointer/touch down: start pinch (two fingers), single-finger drag, or mouse drag.
   * @param {MouseEvent | TouchEvent} event
   */
  #handlePointerDown = (event) => {
    this.#hideHint();
    this.#hideWheelHint();
    if ("touches" in event && event.touches && event.touches.length === 2) {
      event.preventDefault();
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      this.#pinchAnchorCommitted = false;

      const wrapperRect = this.getBoundingClientRect();
      const centerX = ((touch1.clientX + touch2.clientX) / 2) - wrapperRect.left;
      const centerY = ((touch1.clientY + touch2.clientY) / 2) - wrapperRect.top;
      const distance = this.#getDistance(event.touches);

      if (this.#scale > 1) {
        this.#startDistance = distance;
        this.#twoFingerDragStartX = centerX;
        this.#twoFingerDragStartY = centerY;
        this.#twoFingerDragLastX = centerX;
        this.#twoFingerDragLastY = centerY;
        this.#isTwoFingerDrag = false;
        this.#isDragging = true;
      } else {
        this.#startDistance = distance;
        this.#startScale = this.#scale;
        this.#isDragging = false;
        this.#hasDragged = false;
        this.#pinchCenterX = centerX;
        this.#pinchCenterY = centerY;
      }
    } else if ("touches" in event && event.touches && event.touches.length === 1) {
      // Single touch - track for tap detection or drag
      const point = this.#getPoint(event);
      this.#touchStartTime = Date.now();
      this.#touchStartX = point.x;
      this.#touchStartY = point.y;
      this.#startX = point.x - this.#lastX;
      this.#startY = point.y - this.#lastY;
      // If zoomed, enable dragging immediately and prevent default
      if (this.#scale > 1) {
        event.preventDefault();
        this.#isDragging = true;
        this.#hasDragged = false;
        this.classList.add("is-dragging");
      } else {
        // Not zoomed - set dragging flag but don't prevent default (allows tap)
        this.#isDragging = true;
        this.#hasDragged = false;
      }
    } else {
      // Mouse drag start
      event.preventDefault();
      const point = this.#getPoint(event);
      this.#isDragging = true;
      this.#hasDragged = false;
      this.#startX = point.x - this.#lastX;
      this.#startY = point.y - this.#lastY;
      // Add dragging class for cursor change
      if (this.#scale > 1) {
        this.classList.add("is-dragging");
      }
    }
  };

  /**
   * Pointer/touch move: update pinch zoom (pivot between fingers), two-finger pan, or single-finger/mouse pan.
   * @param {MouseEvent | TouchEvent} event
   */
  #handlePointerMove = (event) => {
    const isTouchEvent = "touches" in event;
    if (isTouchEvent && event.touches && event.touches.length === 2) {
      event.preventDefault();
      
      if (!this.#image) return;
      
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;
      
      const wrapperRect = this.getBoundingClientRect();
      const currentCenterX = ((touch1.clientX + touch2.clientX) / 2) - wrapperRect.left;
      const currentCenterY = ((touch1.clientY + touch2.clientY) / 2) - wrapperRect.top;
      const newDistance = this.#getDistance(event.touches);
      const distanceChange = Math.abs(newDistance - this.#startDistance);
      const pinchDeadZone = 18;

      if (this.#scale > 1) {
        const distanceThreshold = 28;

        if (!this.#isTwoFingerDrag && distanceChange >= pinchDeadZone && distanceChange < distanceThreshold) {
          this.#isTwoFingerDrag = true;
          this.#twoFingerDragStartX = currentCenterX - this.#lastX;
          this.#twoFingerDragStartY = currentCenterY - this.#lastY;
        }

        if (this.#isTwoFingerDrag && distanceChange < distanceThreshold * 2) {
          const deltaX = currentCenterX - this.#twoFingerDragLastX;
          const deltaY = currentCenterY - this.#twoFingerDragLastY;
          this.#lastX += deltaX;
          this.#lastY += deltaY;
          this.#twoFingerDragLastX = currentCenterX;
          this.#twoFingerDragLastY = currentCenterY;
          this.#clampTranslation();
          return;
        }
        this.#isTwoFingerDrag = false;
      }

      if (!this.#pinchAnchorCommitted) {
        if (distanceChange < pinchDeadZone) return;
        this.#pinchAnchorCommitted = true;
        this.#startDistance = newDistance;
        this.#startScale = this.#scale;
      }

      const rawScaleChange = newDistance / this.#startDistance;
      const isMobile = window.matchMedia("(max-width: 749px)").matches;
      const zoomSensitivity = isMobile ? 0.4 : 0.15;
      const newScale = Math.max(1, Math.min(5, this.#startScale * (1 + (rawScaleChange - 1) * zoomSensitivity)));

      this.#scale = newScale;
      this.style.setProperty("--drag-zoom-scale", String(this.#scale));
      
      // If zoomed out to minimum (scale = 1), center the image
      if (this.#scale === 1) {
        this.#lastX = 0;
        this.#lastY = 0;
        this.style.setProperty("--drag-zoom-translate-x", "0px");
        this.style.setProperty("--drag-zoom-translate-y", "0px");
      } else {
        // Keep the point under the current pinch center (between the fingers) fixed during zoom.
        // Use current scale/translation to find which image point is under the fingers, then
        // set new translation so that point stays there at newScale (avoids drift).
        const centerScreenX = currentCenterX - wrapperRect.width / 2;
        const centerScreenY = currentCenterY - wrapperRect.height / 2;
        const currentScale = this.#scale;
        const imageX = (centerScreenX - this.#lastX) / currentScale;
        const imageY = (centerScreenY - this.#lastY) / currentScale;
        this.#lastX = centerScreenX - imageX * this.#scale;
        this.#lastY = centerScreenY - imageY * this.#scale;
      }
      
      // Update drag tracking
      this.#twoFingerDragLastX = currentCenterX;
      this.#twoFingerDragLastY = currentCenterY;
      
      this.#updateZoomState();
      this.#clampTranslation();
    } else if (this.#isDragging) {
      // Drag move - handle both mouse and touch
      // For touch events, verify the touch is still active
      if (isTouchEvent) {
        const touches = event.touches;
        if (!touches || touches.length === 0) {
          // No active touches, might have ended
          return;
        }
      }
      
      const point = this.#getPoint(event);
      
      // Only allow dragging when zoomed in
      if (this.#scale > 1) {
        // Always prevent default for touch events when dragging zoomed image
        // This prevents page scrolling while dragging
        if (isTouchEvent) {
          event.preventDefault();
          event.stopPropagation();
        }
        
        const deltaX = Math.abs(point.x - (this.#startX + this.#lastX));
        const deltaY = Math.abs(point.y - (this.#startY + this.#lastY));
        // Mark as dragged if moved more than 5px
        if (deltaX > 5 || deltaY > 5) {
          this.#hasDragged = true;
        }
        // Update position
        this.#lastX = point.x - this.#startX;
        this.#lastY = point.y - this.#startY;
        this.#clampTranslation();
      }
    }
  };

  /**
   * Pointer/touch up: clear drag state; on touch, detect tap (zoom in/out) if no drag.
   * @param {MouseEvent | TouchEvent} event
   */
  #handlePointerUp = (event) => {
    const wasDragging = this.#isDragging;
    this.#isDragging = false;
    this.#startDistance = 0;
    this.#isTwoFingerDrag = false;
    this.#pinchAnchorCommitted = false;
    this.classList.remove("is-dragging");

    if (
      "touches" in event &&
      event.changedTouches &&
      event.changedTouches.length === 1 &&
      !this.#hasDragged &&
      wasDragging
    ) {
      const touchEndTime = Date.now();
      const touchDuration = touchEndTime - this.#touchStartTime;
      const touch = event.changedTouches[0];
      if (touch) {
        const deltaX = Math.abs(touch.clientX - this.#touchStartX);
        const deltaY = Math.abs(touch.clientY - this.#touchStartY);
        if (touchDuration < 300 && deltaX < 10 && deltaY < 10) {
          this.#handleTapZoom(event);
          return;
        }
      }
    }

    this.#scheduleHint();
  };

  /**
   * Applies tap-to-zoom: zoom in to 2x at (tapX, tapY) or reset to 1x if already zoomed.
   * @param {number} tapX - X relative to wrapper left
   * @param {number} tapY - Y relative to wrapper top
   */
  #applyTapZoom(tapX, tapY) {
    this.#hideHint();
    this.#hideWheelHint();
    if (this.#scale === 1) {
      if (!this.#image) return;
      const wrapperRect = this.getBoundingClientRect();
      const imageX = (tapX - wrapperRect.width / 2) / 1;
      const imageY = (tapY - wrapperRect.height / 2) / 1;
      this.#scale = 2;
      this.style.setProperty("--drag-zoom-scale", String(this.#scale));
      this.#lastX = tapX - wrapperRect.width / 2 - imageX * this.#scale;
      this.#lastY = tapY - wrapperRect.height / 2 - imageY * this.#scale;
      this.#updateZoomState();
      this.#clampTranslation();
      this.#scheduleHint();
    } else {
      this.#scale = 1;
      this.#lastX = 0;
      this.#lastY = 0;
      this.style.setProperty("--drag-zoom-scale", "1");
      this.style.setProperty("--drag-zoom-translate-x", "0px");
      this.style.setProperty("--drag-zoom-translate-y", "0px");
      this.#updateZoomState();
    }
  }

  /**
   * Resolves tap/click coordinates and calls #applyTapZoom.
   * @param {MouseEvent | TouchEvent} event
   */
  #handleTapZoom = (event) => {
    event.preventDefault();
    const wrapperRect = this.getBoundingClientRect();
    let tapX = 0;
    let tapY = 0;
    if ("changedTouches" in event && event.changedTouches && event.changedTouches.length > 0 && event.changedTouches[0]) {
      const t = event.changedTouches[0];
      tapX = t.clientX - wrapperRect.left;
      tapY = t.clientY - wrapperRect.top;
    } else if ("clientX" in event) {
      tapX = event.clientX - wrapperRect.left;
      tapY = event.clientY - wrapperRect.top;
    }
    this.#applyTapZoom(tapX, tapY);
  };

  /**
   * Click: zoom in 2x at click or reset to 1x; ignored if the pointer moved (drag).
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    this.#hideHint();
    this.#hideWheelHint();
    // Only handle click if it wasn't a drag
    if (this.#hasDragged) {
      this.#hasDragged = false;
      return;
    }

    // If not zoomed, zoom in to 2x centered on click position
    if (this.#scale === 1) {
      event.preventDefault();
      if (!this.#image) return;
      
      // Get click position relative to the wrapper
      const wrapperRect = this.getBoundingClientRect();
      const clickX = event.clientX - wrapperRect.left;
      const clickY = event.clientY - wrapperRect.top;
      
      // Calculate the point in image coordinates (at scale 1)
      const imageX = (clickX - wrapperRect.width / 2) / 1;
      const imageY = (clickY - wrapperRect.height / 2) / 1;
      
      // Zoom to 2x
      this.#scale = 2;
      this.style.setProperty("--drag-zoom-scale", String(this.#scale));
      
      // Adjust translation to keep the click point in the same place
      this.#lastX = clickX - wrapperRect.width / 2 - imageX * this.#scale;
      this.#lastY = clickY - wrapperRect.height / 2 - imageY * this.#scale;
      
      this.#updateZoomState();
      this.#clampTranslation();
      this.#scheduleHint();
    } else {
      // If zoomed, reset zoom on click
      event.preventDefault();
      this.#scale = 1;
      this.#lastX = 0;
      this.#lastY = 0;
      this.style.setProperty("--drag-zoom-scale", "1");
      this.style.setProperty("--drag-zoom-translate-x", "0px");
      this.style.setProperty("--drag-zoom-translate-y", "0px");
      this.#updateZoomState();
    }
  };

  /**
   * Returns client position of the primary pointer (first touch or mouse).
   * @param {MouseEvent | TouchEvent} event
   * @returns {{ x: number, y: number }}
   */
  #getPoint(event) {
    if (
      "touches" in event &&
      event.touches &&
      event.touches.length > 0 &&
      event.touches[0]
    ) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    if ("clientX" in event) {
      return { x: event.clientX, y: event.clientY };
    }
    return { x: 0, y: 0 };
  }

  /**
   * Returns distance between first two touches in px.
   * @param {TouchList} touches
   * @returns {number}
   */
  #getDistance(touches) {
    if (touches.length < 2 || !touches[0] || !touches[1]) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /** Schedules show/hide of the wheel hint when not zoomed (desktop). */
  #initWheelHintVisibility() {
    this.#scheduleWheelHint();

    // Track mouse movement to only hide after actual movement
    /** @type {ReturnType<typeof setTimeout> | null} */
    let wheelMouseMoveTimeout = null;
    /** @type {number | null} */
    let lastMouseX = null;
    /** @type {number | null} */
    let lastMouseY = null;
    let hasActuallyMoved = false;

    this.addEventListener("mousemove", (event) => {
      const currentX = event.clientX;
      const currentY = event.clientY;

      // Only consider it movement if mouse actually moved (not just hover)
      if (lastMouseX !== null && lastMouseY !== null) {
        const deltaX = Math.abs(currentX - lastMouseX);
        const deltaY = Math.abs(currentY - lastMouseY);
        if (deltaX > 3 || deltaY > 3) {
          hasActuallyMoved = true;
          this.#hideWheelHint();
        }
      }

      lastMouseX = currentX;
      lastMouseY = currentY;

      if (wheelMouseMoveTimeout) {
        clearTimeout(wheelMouseMoveTimeout);
      }
      // Reschedule hint after mouse stops moving for 4 seconds
      wheelMouseMoveTimeout = setTimeout(() => {
        if (this.#scale === 1 && hasActuallyMoved) {
          this.#scheduleWheelHint();
        }
      }, 4000);
    });

    this.addEventListener("mouseleave", () => {
      lastMouseX = null;
      lastMouseY = null;
      hasActuallyMoved = false;
      if (wheelMouseMoveTimeout) {
        clearTimeout(wheelMouseMoveTimeout);
        wheelMouseMoveTimeout = null;
      }
      if (this.#scale === 1) {
        this.#scheduleWheelHint();
      }
    });
  }

  #scheduleWheelHint() {
    this.#hideWheelHint();
    if (this.#scale === 1 && !this.#isDragging) {
      this.#wheelHintTimeout = setTimeout(() => {
        if (this.#scale === 1 && !this.#isDragging && this.#wheelHintElement) {
          if (this.#wheelHintElement instanceof HTMLElement) {
            // Use the same animation as drag hint
            this.#wheelHintElement.style.animation =
              "showDragHint 0.5s ease-out forwards";
            this.#wheelHintElement.style.opacity = "1";

            // Hide after 4 seconds, then reschedule to show again
            this.#wheelHintHideTimeout = setTimeout(() => {
              this.#hideWheelHint();
              // Reschedule to show again after 2 seconds
              setTimeout(() => {
                if (this.#scale === 1 && !this.#isDragging) {
                  this.#scheduleWheelHint();
                }
              }, 2000);
            }, 4000);
          }
        }
      }, 2000);
    }
  }

  #hideWheelHint() {
    if (this.#wheelHintTimeout) {
      clearTimeout(this.#wheelHintTimeout);
      this.#wheelHintTimeout = null;
    }
    if (this.#wheelHintHideTimeout) {
      clearTimeout(this.#wheelHintHideTimeout);
      this.#wheelHintHideTimeout = null;
    }
    if (
      this.#wheelHintElement &&
      this.#wheelHintElement instanceof HTMLElement
    ) {
      this.#wheelHintElement.style.animation = "none";
      this.#wheelHintElement.style.opacity = "0";
    }
  }

  /** Toggles is-zoomed class and schedules pinch or wheel hint depending on viewport. */
  #updateZoomState() {
    const isMobile = window.matchMedia("(max-width: 749px)").matches;
    if (this.#scale > 1) {
      this.classList.add("is-zoomed");
      this.#scheduleHint();
      this.#hideWheelHint();
    } else {
      this.classList.remove("is-zoomed");
      this.#hideHint();
      // On mobile, show pinch hint; on desktop/tablet, show wheel hint
      if (isMobile) {
        this.#scheduleHint();
      } else {
        this.#scheduleWheelHint();
      }
    }
  }

  /** Clamps --drag-zoom-translate-* so the image cannot be panned outside the visible area. */
  #clampTranslation() {
    if (!this.#image) return;

    const wrapperRect = this.getBoundingClientRect();
    const renderedWidth = this.#image.clientWidth || this.#image.naturalWidth;
    const renderedHeight =
      this.#image.clientHeight || this.#image.naturalHeight;

    // Calculate scaled image dimensions
    const imgWidth = renderedWidth * this.#scale;
    const imgHeight = renderedHeight * this.#scale;

    // Calculate maximum allowed translation (half the difference between scaled image and wrapper)
    const maxX = Math.max(0, (imgWidth - wrapperRect.width) / 2);
    const maxY = Math.max(0, (imgHeight - wrapperRect.height) / 2);

    // Clamp translation values (don't divide by scale - translation is already in pixels)
    const clampedX = Math.min(maxX, Math.max(-maxX, this.#lastX));
    const clampedY = Math.min(maxY, Math.max(-maxY, this.#lastY));

    // Update the actual translation values
    this.#lastX = clampedX;
    this.#lastY = clampedY;

    this.style.setProperty("--drag-zoom-translate-x", `${clampedX}px`);
    this.style.setProperty("--drag-zoom-translate-y", `${clampedY}px`);
  }

  /**
   * Resets zoom to 1x and centers the image. Used by zoom-dialog when opening/closing.
   */
  resetZoom() {
    this.#hideHint();
    this.#hideWheelHint();
    this.#scale = 1;
    this.#lastX = 0;
    this.#lastY = 0;
    this.#isDragging = false;
    this.#hasDragged = false;
    this.#isTwoFingerDrag = false;
    this.#pinchAnchorCommitted = false;
    this.style.setProperty("--drag-zoom-scale", "1");
    this.style.setProperty("--drag-zoom-translate-x", "0px");
    this.style.setProperty("--drag-zoom-translate-y", "0px");
    this.#updateZoomState();
  }
}

customElements.define("drag-zoom-wrapper", DragZoomWrapper);
